import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { getRandomId } from '../../utils/get-random-id';
import { GreyBoxSize } from '../grey-box/grey-box.types';

/**
 * @slot - select element (required)
 * @slot message - Used to place a wb-control-hint or wb-control-error below the input (optional)
 * @slot label - Text placed within the inner label element (required)
 */
@Component({
  tag: 'wb-select',
  styleUrl: 'select.scss',
  shadow: true,
})
export class Select implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** @deprecated label for accessibility (deprecated in favor of the "label" slot) */
  @Prop()
  public label?: string;

  /** The size of the input */
  @Prop({ reflect: true })
  public size?: GreyBoxSize = 'l';

  /** Displays compact variant */
  @Prop({ reflect: true })
  public compact: boolean = false;

  /** Marks input control as successful */
  @Prop({ reflect: true })
  public success: boolean = false;

  /** marks control as invalid. automatically set by slotted wb-control-error */
  @Prop({ reflect: true, mutable: true })
  public error: boolean = false;

  /** marks control as disabled. DON'T MANUALLY USE. Automatically set via slotted select */
  @Prop({ reflect: true, mutable: true })
  public disabled: boolean = false;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public active: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public filled: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** label describing to a screenreader the state of a validation */
  @Prop({ reflect: true })
  public readerValidationMessage?: string = '';

  /** aria-label describing action of icon that user can click */
  @Prop({ reflect: true })
  public buttonActionLabel: string = '';

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  private TagNames = transformTagNames(this, 'wb-select', ['wb-control-error', 'wb-control-hint', 'wb-icon', 'wb-grey-box']);

  private selectEl!: HTMLSelectElement;
  private mutationObserver: MutationObserver;
  private skipNextSlotChangeHandler: boolean = false;

  public connectedCallback(): void {
    this.active = Boolean(document.activeElement === this.selectEl);
  }

  private handleMessageSlotChange = (): void => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.theme = this.theme;
      messageEl.id = messageEl.id || `message-${getRandomId()}`;
      this.selectEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.selectEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private handleSlotChange = (): void => {
    if (this.skipNextSlotChangeHandler) {
      this.skipNextSlotChangeHandler = false;
      return;
    }

    this.selectEl = this.el.querySelector('select');

    if (!this.selectEl) {
      return;
    }

    this.updateDisabled();
    this.updateFilled();
    this.setSelectId();
    this.updateLightDomLabel();
    this.registerDisabledObserver();
    this.registerFocusEventListeners();
  };

  private setSelectId(): void {
    this.selectEl.id = this.selectEl.id || `select-${getRandomId()}`;
  }

  private registerDisabledObserver(): void {
    if (this.mutationObserver) {
      return;
    }

    this.mutationObserver = new MutationObserver(this.updateDisabled);
    this.mutationObserver.observe(this.selectEl, {
      subtree: false,
      attributeFilter: ['disabled'],
    });
  }

  private registerFocusEventListeners(): void {
    this.selectEl.addEventListener('focus', this.onFocus);
    this.selectEl.addEventListener('blur', this.onBlur);
    this.selectEl.addEventListener('change', this.updateFilled);
  }

  private updateFilled = (): void => {
    this.filled = Boolean(this.selectEl.value);
  };

  private updateDisabled = (): void => {
    this.disabled = this.selectEl.disabled;
  };

  private onFocus = (): void => {
    this.active = true;
  };

  private onBlur = (): void => {
    this.active = false;
  };

  private getContentFromSlot = (): string => {
    const slot: HTMLSlotElement = this.el.querySelector('[slot="label"]');
    const labelContent = slot.textContent;

    return labelContent || '';
  }

  private getLabelContent = (): string => {
    return this.label ? this.label : this.getContentFromSlot();
  }

  @Watch('label')
  public updateLightDomLabel(): void {
    const labelEl = this.el.querySelector('label');
    const labelContent = this.getLabelContent();

    if (labelEl) {
      labelEl.textContent = labelContent;
    } else {
      const newLabelEl = document.createElement('label');
      newLabelEl.hidden = true;
      newLabelEl.textContent = labelContent;
      newLabelEl.htmlFor = this.selectEl.id;

      this.skipNextSlotChangeHandler = true; // setting this to avoid the default slot change handler from running twice
      this.el.appendChild(newLabelEl);
    }

    this.selectEl.setAttribute('aria-label', labelContent);
  }

  public render(): JSX.Element {
    const { WbGreyBox } = this.TagNames;

    return (
      <Host class="wb-select" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbGreyBox size={this.size} active={this.active} error={this.error} success={this.success} disabled={this.disabled} hasCaretIcon hasStatusIcons readerValidationMessage={this.readerValidationMessage} buttonActionLabel={this.buttonActionLabel}>
          <label htmlFor="input" class="label">
            {this.label ? this.label : <slot name="label" />}
          </label>
          <div class="default-slot-container">
            <slot onSlotchange={this.handleSlotChange} />
          </div>
        </WbGreyBox>
        <slot name="message" onSlotchange={this.handleMessageSlotChange} />
      </Host>
    );
  }
}
